import React, { useState, useEffect } from 'react';
import { useLeadFormStore } from '../../../store/leadFormStore';
import { StepProps } from '../types';
import { useAuth } from '../../../contexts/AuthContext';
import { projectService } from '../../../services/project.service';
import { Project } from '../../../types/project.types';
import { toast } from 'react-hot-toast';

const ParentInfoStep: React.FC<StepProps> = ({ onNext }) => {
  const { 
    formData, 
    setParentData, 
    errors, 
    setError, 
    clearError,
    markStepCompleted,
    saveToStorage,
    projectId,
    setProjectId 
  } = useLeadFormStore();

  const { parent } = formData;
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  // Load projects on mount
  useEffect(() => {
    const loadProjects = async () => {
      if (!currentUser) return;
      
      try {
        const userProjects = await projectService.getUserProjects(currentUser.uid);
        setProjects(userProjects);
        
        // Pre-select first project if none selected
        if (userProjects.length > 0 && !projectId) {
          setProjectId(userProjects[0].id);
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        toast.error('Failed to load projects');
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, [currentUser, projectId, setProjectId]);

  const handleChange = (field: string, value: string) => {
    setParentData({ [field]: value });
    clearError(`parent.${field}`);
  };

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone: string) => {
    const re = /^\d{10}$/;
    return re.test(phone.replace(/\D/g, ''));
  };

  const formatPhoneNumber = (value: string) => {
    const phone = value.replace(/\D/g, '');
    if (phone.length <= 3) return phone;
    if (phone.length <= 6) return `(${phone.slice(0, 3)}) ${phone.slice(3)}`;
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
  };

  // Validation is handled by FormNavigation now
  React.useEffect(() => {
    const isValid = 
      projectId && // Add project validation
      parent.firstName.trim() !== '' &&
      parent.lastName.trim() !== '' &&
      parent.email.trim() !== '' &&
      validateEmail(parent.email) &&
      parent.phone.trim() !== '' &&
      validatePhone(parent.phone);
      
    if (isValid) {
      markStepCompleted('parent');
    }
  }, [parent, projectId, markStepCompleted]);

  return (
    <div className="space-y-6">
      {/* Project Selection */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900">Project Selection</h3>
        <p className="mt-1 text-sm text-gray-600 mb-4">
          Select the project this lead belongs to.
        </p>
        
        {loadingProjects ? (
          <div className="text-gray-500">Loading projects...</div>
        ) : projects.length === 0 ? (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-yellow-800">No projects available. Contact your administrator.</p>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Project <span className="text-red-500">*</span>
            </label>
            <select
              value={projectId || ''}
              onChange={(e) => setProjectId(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              required
            >
              <option value="">Select a project...</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900">Parent Information</h3>
        <p className="mt-1 text-sm text-gray-600">
          Enter the primary contact information for the parent or guardian.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={parent.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            className={`mt-1 block w-full rounded-md shadow-sm ${
              errors['parent.firstName'] 
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors['parent.firstName'] && (
            <p className="mt-1 text-sm text-red-600">{errors['parent.firstName']}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={parent.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            className={`mt-1 block w-full rounded-md shadow-sm ${
              errors['parent.lastName'] 
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors['parent.lastName'] && (
            <p className="mt-1 text-sm text-red-600">{errors['parent.lastName']}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={parent.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className={`mt-1 block w-full rounded-md shadow-sm ${
              errors['parent.email'] 
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors['parent.email'] && (
            <p className="mt-1 text-sm text-red-600">{errors['parent.email']}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={formatPhoneNumber(parent.phone)}
            onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, ''))}
            placeholder="(555) 555-5555"
            className={`mt-1 block w-full rounded-md shadow-sm ${
              errors['parent.phone'] 
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors['parent.phone'] && (
            <p className="mt-1 text-sm text-red-600">{errors['parent.phone']}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Preferred Contact Method
          </label>
          <select
            value={parent.preferredContact}
            onChange={(e) => handleChange('preferredContact', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="phone">Phone</option>
            <option value="email">Email</option>
            <option value="text">Text Message</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Preferred Language
          </label>
          <select
            value={parent.language || ''}
            onChange={(e) => handleChange('language', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">Not specified</option>
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default ParentInfoStep;
